/**
 * ตั้งค่าสิทธิ์ (OWNER เท่านั้น) — รวม 3 แท็บ:
 *   [HR]         มอบอำนาจ HR ทั้งบริษัท 3 ระดับ + เฉพาะคน (เดิม)
 *   [หัวหน้างาน]  สิทธิ์ตามบทบาท SUPERVISOR — เลือก ดู/แก้ ต่อโมดูล
 *   [ผู้จัดการ]    สิทธิ์ตามบทบาท MANAGER — เลือก ดู/แก้ ต่อโมดูล
 * แท็บบทบาท: ขอบเขตคงที่ = เห็นเฉพาะลูกทีมตัวเอง (ทั้งสายใต้บังคับบัญชา). บันทึกมีผลทันที.
 */
import { useState, useEffect, type ReactNode } from 'react';
import { ShieldCheck, Check, Clock3, X, AlertTriangle, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PillTabs } from '../../components/layout/HubHeader';
import {
  useHrPermissions, useSaveHrPermissions, type CapGrant,
  useTeamRolePermissions, useSaveTeamRolePermissions, type RolePermissionMap,
} from '../../hooks';
import { useToast } from '../../components/ui/toast';

const TIERS = [
  { id: 1, name: 'ผู้ช่วย', tagline: 'ดู + งานประจำวันเท่านั้น', risk: 'ปลอดภัยสุด — HR ตั้งค่า/ทำเงินเดือนไม่ได้ ทุกอย่างเสนอเจ้าของ' },
  { id: 2, name: 'ดูแลระบบ', tagline: 'ตั้งค่า/ทำเงินเดือนได้ · จุดกระทบเงินรอเจ้าของอนุมัติ', risk: 'สมดุล — ลด friction เจ้าของ แต่แก้เงินเดือน/ปิดงวด/เชิญผู้จัดการต้องอนุมัติ (แนะนำ)' },
  { id: 3, name: 'เต็มสิทธิ์', tagline: 'ทำเองได้หมด', risk: 'ไว้ใจเต็มที่ — HR ทำเองทุกอย่างรวมปิดงวด/แก้เงินเดือน (เชิญเจ้าของยังต้องอนุมัติ)' },
] as const;

const GRANT_ICON: Record<CapGrant, ReactNode> = {
  allow: <Check className="w-3.5 h-3.5 text-teal-600" />,
  pending: <Clock3 className="w-3.5 h-3.5 text-amber-500" />,
  deny: <X className="w-3.5 h-3.5 text-gray-300" />,
};
const GRANT_LABEL: Record<CapGrant, string> = { allow: 'ทำได้', pending: 'รออนุมัติ', deny: 'ไม่ได้' };

type Tab = 'hr' | 'SUPERVISOR' | 'MANAGER';

export function OwnerHrPermissions() {
  const [tab, setTab] = useState<Tab>('hr');
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'hr', label: 'HR' },
    { key: 'SUPERVISOR', label: 'หัวหน้างาน' },
    { key: 'MANAGER', label: 'ผู้จัดการ' },
  ];

  return (
    <AdminLayout title="ตั้งค่าสิทธิ์">
      <div className="max-w-2xl mx-auto space-y-5 pb-10">
        <PillTabs tabs={tabs} active={tab} onChange={setTab} />
        {tab === 'hr' && <HrTab />}
        {tab !== 'hr' && <RoleTab role={tab} />}
      </div>
    </AdminLayout>
  );
}

// === แท็บ HR (เดิม) ===
function HrTab() {
  const { data, isLoading } = useHrPermissions();
  const save = useSaveHrPermissions();
  const toast = useToast();
  const [tier, setTier] = useState<number>(2);
  const [overrides, setOverrides] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (data) {
      setTier(data.default_tier);
      setOverrides(Object.fromEntries(data.members.map((m) => [m.user_id, m.tier])));
    }
  }, [data]);

  function saveDefault() {
    save.mutate({ default_tier: tier }, {
      onSuccess: () => toast.success('บันทึกระดับสิทธิ์ HR ของบริษัทแล้ว (มีผลทันที)'),
      onError: () => toast.error('บันทึกไม่สำเร็จ'),
    });
  }
  function saveOverride(userId: string, t: number | null) {
    setOverrides((o) => ({ ...o, [userId]: t }));
    save.mutate({ overrides: [{ user_id: userId, tier: t }] }, {
      onSuccess: () => toast.success('บันทึกสิทธิ์รายคนแล้ว'),
      onError: () => toast.error('บันทึกไม่สำเร็จ'),
    });
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500 px-1">
        กำหนดว่าให้ HR ทำอะไรแทนคุณได้บ้าง — เลือกระดับเดียวใช้ทั้งบริษัท แล้วปรับเฉพาะคนได้.
        จุดกระทบเงิน (แก้เงินเดือน/ปิดงวด) ที่เป็น “รออนุมัติ” จะส่งคำขอมาให้คุณเสมอ. เปลี่ยนได้ตลอด.
      </p>

      {isLoading && <div className="card p-6 text-center text-gray-400">กำลังโหลด…</div>}

      {data && (
        <>
          {/* ระดับทั้งบริษัท */}
          <section className="space-y-3">
            <h2 className="font-semibold text-ink flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal" /> ระดับมอบอำนาจ HR (ทั้งบริษัท)</h2>
            {TIERS.map((t) => {
              const grants = data.tiers[String(t.id)] ?? {};
              const sel = tier === t.id;
              return (
                <button key={t.id} type="button" onClick={() => setTier(t.id)}
                  className={`card p-4 w-full text-left ${sel ? 'ring-2 ring-teal' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full border-2 ${sel ? 'border-teal bg-teal' : 'border-gray-300'}`} />
                    <span className="font-medium text-ink">{t.id}. HR {t.name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 ml-6">{t.tagline}</p>
                  <p className="text-xs text-gray-400 mt-1 ml-6 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{t.risk}</p>
                  {sel && (
                    <div className="mt-3 ml-6 grid grid-cols-1 gap-0.5">
                      {data.capabilities.map((c) => (
                        <div key={c.key} className="flex items-center gap-2 text-xs">
                          {GRANT_ICON[grants[c.key] ?? 'deny']}
                          <span className="text-gray-600 flex-1">{c.label}</span>
                          <span className="text-gray-400">{GRANT_LABEL[grants[c.key] ?? 'deny']}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
            <button type="button" onClick={saveDefault} disabled={save.isPending || tier === data.default_tier}
              className="btn-cta w-full justify-center flex">
              บันทึกระดับบริษัท
            </button>
          </section>

          {/* override รายคน */}
          {data.members.length > 0 && (
            <section className="space-y-2">
              <h2 className="font-semibold text-ink">กำหนดเฉพาะคน (ถ้าต้องการต่างจากบริษัท)</h2>
              <div className="card divide-y divide-gray-100">
                {data.members.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-xs text-gray-400">{m.role}{m.emp_code ? ` · ${m.emp_code}` : ''}</p>
                    </div>
                    <select
                      value={overrides[m.user_id] ?? ''}
                      onChange={(e) => saveOverride(m.user_id, e.target.value === '' ? null : Number(e.target.value))}
                      className="input text-sm py-1.5 w-auto"
                    >
                      <option value="">ตามบริษัท ({tier})</option>
                      <option value="1">1 ผู้ช่วย</option>
                      <option value="2">2 ดูแลระบบ</option>
                      <option value="3">3 เต็มสิทธิ์</option>
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400">“ตามบริษัท” = ใช้ระดับเดียวกับที่ตั้งด้านบน</p>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// === แท็บสิทธิ์ตามบทบาท (หัวหน้างาน / ผู้จัดการ) ===
const ROLE_NAME: Record<'SUPERVISOR' | 'MANAGER', string> = {
  SUPERVISOR: 'หัวหน้างาน',
  MANAGER: 'ผู้จัดการ',
};

function RoleTab({ role }: { role: 'SUPERVISOR' | 'MANAGER' }) {
  const { data, isLoading } = useTeamRolePermissions();
  const save = useSaveTeamRolePermissions();
  const toast = useToast();
  const [perm, setPerm] = useState<RolePermissionMap>({});

  const current = data?.roles.find((r) => r.role_target === role);
  useEffect(() => {
    if (current) setPerm(current.permissions);
  }, [current]);

  function toggle(moduleKey: string, action: 'view' | 'edit') {
    setPerm((p) => {
      const cur = p[moduleKey] ?? { view: false, edit: false };
      const next = { ...cur, [action]: !cur[action] };
      // แก้ได้ต้องดูได้ด้วย: ติ๊กแก้ → เปิดดูอัตโนมัติ; ปิดดู → ปิดแก้ด้วย
      if (action === 'edit' && next.edit) next.view = true;
      if (action === 'view' && !next.view) next.edit = false;
      return { ...p, [moduleKey]: next };
    });
  }

  function onSave() {
    save.mutate({ role_target: role, permissions: perm }, {
      onSuccess: () => toast.success(`บันทึกสิทธิ์${ROLE_NAME[role]}แล้ว (มีผลทันที)`),
      onError: () => toast.error('บันทึกไม่สำเร็จ'),
    });
  }

  if (isLoading) return <div className="card p-6 text-center text-gray-400">กำลังโหลด…</div>;
  if (!data) return null;

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500 px-1">
        กำหนดว่า{ROLE_NAME[role]}ทำอะไรได้บ้าง — เลือกอิสระว่าแต่ละเรื่อง “ดู” อย่างเดียว หรือ “แก้” ได้ด้วย.
        ตั้งครั้งเดียวใช้กับทุกคนที่เป็น{ROLE_NAME[role]}.
      </p>

      <div className="card p-3 flex items-start gap-2 bg-teal-50/50">
        <Users className="w-4 h-4 text-teal mt-0.5 shrink-0" />
        <div className="text-sm">
          <span className="font-medium text-ink">ขอบเขต:</span>{' '}
          <span className="text-gray-600">{data.scope_label}</span>
          <p className="text-xs text-gray-400 mt-0.5">
            {ROLE_NAME[role]}จะเห็นและจัดการได้เฉพาะลูกทีมของตัวเอง รวมลูกทีมของลูกทีมด้วย — ไม่เห็นทั้งบริษัท
          </p>
        </div>
      </div>

      {/* matrix: แถว = โมดูล, คอลัมน์ = ดู / แก้ */}
      <section className="card overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 px-4 py-2.5 border-b border-gray-100 text-xs text-gray-400">
          <span>เรื่อง</span>
          <span className="w-14 text-center">ดู</span>
          <span className="w-14 text-center">แก้</span>
        </div>
        {data.modules.map((m) => {
          const mp = perm[m.key] ?? { view: false, edit: false };
          return (
            <div key={m.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 px-4 py-3 border-b border-gray-50 last:border-0">
              <span className="text-sm text-ink">{m.label}</span>
              <label className="w-14 flex justify-center">
                <input type="checkbox" checked={mp.view} onChange={() => toggle(m.key, 'view')}
                  className="w-4 h-4 rounded border-gray-300 text-teal focus:ring-teal" />
              </label>
              <label className="w-14 flex justify-center">
                <input type="checkbox" checked={mp.edit} onChange={() => toggle(m.key, 'edit')}
                  className="w-4 h-4 rounded border-gray-300 text-teal focus:ring-teal" />
              </label>
            </div>
          );
        })}
      </section>

      <p className="text-xs text-gray-400 px-1">“แก้” จะเปิด “ดู” ให้อัตโนมัติ — แก้ได้ก็ต้องดูได้</p>

      <button type="button" onClick={onSave} disabled={save.isPending}
        className="btn-cta w-full justify-center flex">
        บันทึกสิทธิ์{ROLE_NAME[role]}
      </button>
    </div>
  );
}
